/**
 * Shared constants and configuration values used across components
 * Centralizes non-style values for consistency and maintainability
 *
 * Companion to shared-variants.ts which handles style/class utilities
 */

// ============================================================================
// Z-INDEX SCALE
// ============================================================================

/**
 * Z-index layering system
 * Ensures consistent stacking order across the application
 */
export const Z_INDEX = {
  /** Base level - regular content */
  base: 0,
  /** Dropdowns, popovers, tooltips */
  dropdown: 50,
  /** Sticky elements */
  sticky: 100,
  /** Fixed elements like headers */
  fixed: 200,
  /** Modal backdrop */
  modalBackdrop: 300,
  /** Modal content */
  modal: 400,
  /** Notifications, toasts */
  notification: 500,
  /** Tooltips that should appear above modals */
  tooltip: 600,
} as const;

// ============================================================================
// ANIMATION/TRANSITION DURATIONS
// ============================================================================

/**
 * Standard animation/transition durations in milliseconds
 * Provides consistent timing across the application
 */
export const ANIMATION_DURATION = {
  /** Ultra fast - 100ms - for subtle state changes */
  ultraFast: 100,
  /** Fast - 200ms - for most UI transitions */
  fast: 200,
  /** Normal - 300ms - for standard animations */
  normal: 300,
  /** Slow - 500ms - for emphasized animations */
  slow: 500,
} as const;

/**
 * Tailwind duration classes
 * Maps to the duration constants above
 */
export const DURATION_CLASSES = {
  ultraFast: 'duration-100',
  fast: 'duration-200',
  normal: 'duration-300',
  slow: 'duration-500',
} as const;

// ============================================================================
// COMPONENT-SPECIFIC DIMENSIONS
// ============================================================================

/**
 * Dropdown/select maximum heights
 * Prevents dropdowns from becoming too tall
 */
export const DROPDOWN_MAX_HEIGHT = {
  s: 'max-h-[200px]',
  m: 'max-h-[240px]',
  l: 'max-h-[280px]',
  xl: 'max-h-[320px]',
} as const;

/**
 * Progress bar heights
 * Standard heights for linear progress indicators
 */
export const PROGRESS_HEIGHT = {
  thin: 'h-[2px]',
  normal: 'h-1',
  thick: 'h-2',
} as const;

/**
 * Divider/separator heights
 * Standard heights for horizontal dividers
 */
export const DIVIDER_HEIGHT = {
  thin: 'h-px',
  normal: 'h-[1px]',
  thick: 'h-0.5',
} as const;

/**
 * Highlight bar heights
 * Used in Alert and similar components
 */
export const HIGHLIGHT_HEIGHT = {
  thin: 'h-1',
  normal: 'h-3',
  thick: 'h-4',
} as const;

// ============================================================================
// ANIMATION CLASSES
// ============================================================================

/**
 * Common animation classes
 * Reusable animation utilities
 */
export const ANIMATION_CLASSES = {
  /** Spinning animation for loaders */
  spin: 'animate-spin',
  /** Pulse animation for loading states */
  pulse: 'animate-pulse',
  /** Bounce animation */
  bounce: 'animate-bounce',
  /** Accordion slide down */
  accordionDown: 'animate-accordion-down',
  /** Accordion slide up */
  accordionUp: 'animate-accordion-up',
} as const;

/**
 * Transition property classes
 * Common transition combinations
 */
export const TRANSITION_CLASSES = {
  /** All properties */
  all: 'transition-all',
  /** Colors only */
  colors: 'transition-colors',
  /** Opacity only */
  opacity: 'transition-opacity',
  /** Transform only */
  transform: 'transition-transform',
  /** Shadow only */
  shadow: 'transition-shadow',
} as const;

// ============================================================================
// RADIX UI ANIMATION PATTERNS
// ============================================================================

/**
 * Radix UI data attribute animation patterns
 * Standard animations for Radix components
 */
export const RADIX_ANIMATIONS = {
  /** Fade in on open */
  fadeIn: 'data-open:animate-in data-open:fade-in-0',
  /** Fade out on close */
  fadeOut: 'data-closed:animate-out data-closed:fade-out-0',
  /** Zoom in on open */
  zoomIn: 'data-open:zoom-in-95',
  /** Zoom out on close */
  zoomOut: 'data-closed:zoom-out-95',
  /** Slide in from top */
  slideFromTop: 'data-[side=top]:slide-in-from-bottom-2',
  /** Slide in from bottom */
  slideFromBottom: 'data-[side=bottom]:slide-in-from-top-2',
  /** Slide in from left */
  slideFromLeft: 'data-[side=left]:slide-in-from-right-2',
  /** Slide in from right */
  slideFromRight: 'data-[side=right]:slide-in-from-left-2',
  /** Complete fade + zoom combo */
  fadeZoom:
    'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
} as const;

// ============================================================================
// COMMON DEBOUNCE/THROTTLE DELAYS
// ============================================================================

/**
 * Standard debounce/throttle delays in milliseconds
 * For search inputs, resize handlers, etc.
 */
export const DELAY = {
  /** Very short delay - 150ms - for instant feedback */
  short: 150,
  /** Medium delay - 300ms - for search inputs */
  medium: 300,
  /** Long delay - 500ms - for expensive operations */
  long: 500,
  /** Extra long delay - 1000ms - for very expensive operations */
  extraLong: 1000,
} as const;

// ============================================================================
// BREAKPOINT VALUES (for JS usage)
// ============================================================================

/**
 * Tailwind breakpoint values in pixels
 * For use in JavaScript/TypeScript logic (e.g., window.matchMedia)
 *
 * Note: These match the Tailwind default breakpoints
 * For CSS usage, use Tailwind's responsive prefixes (sm:, md:, etc.)
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

// ============================================================================
// COMMON SIZE VALUES
// ============================================================================

/**
 * Common minimum width values for dropdowns, popovers, etc.
 */
export const MIN_WIDTH = {
  dropdown: 'min-w-[96px]',
  popover: 'min-w-[200px]',
  modal: 'min-w-[320px]',
} as const;

/**
 * Full size utility
 * Used for images, containers that fill parent
 */
export const FULL_SIZE = 'size-full' as const;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type ZIndexKey = keyof typeof Z_INDEX;
export type AnimationDurationKey = keyof typeof ANIMATION_DURATION;
export type DropdownMaxHeightKey = keyof typeof DROPDOWN_MAX_HEIGHT;
export type BreakpointKey = keyof typeof BREAKPOINTS;
